import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Turnstile from "../components/Turnstile";
import { login, setSession, clearSession, errorMessage, getAuthToken } from "../shared/api";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function LoginPage() {
    const navigate = useNavigate();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [captchaToken, setCaptchaToken] = useState("");
    const [captchaReset, setCaptchaReset] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const usernameRef = useRef(null);

    useEffect(() => {
        if (getAuthToken()) navigate("/dashboard", { replace: true });
        else usernameRef.current?.focus();
    }, [navigate]);

    const handleVerify = useCallback((token) => setCaptchaToken(token), []);
    const handleExpire = useCallback(() => setCaptchaToken(""), []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!captchaToken) {
            setError("Please complete the captcha");
            return;
        }
        try {
            setLoading(true);
            setError("");
            clearSession();
            const { data } = await login({ username, password, captcha_token: captchaToken });
            setSession(data);
            navigate("/dashboard", { replace: true });
        } catch (err) {
            setError(errorMessage(err, "Login failed"));
            setCaptchaToken("");
            setCaptchaReset((n) => n + 1);
        } finally {
            setLoading(false);
        }
    };

    const inputClass = "rounded-xl border border-white/20 bg-white/5 px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-rose-500 transition";

    return (
        <div className="min-h-screen bg-neutral-950 flex items-center justify-center px-6 py-12">
            <div className="w-full max-w-md">
                <div className="mb-8 flex flex-col items-center text-center">
                    <img src="/mavx.svg" alt="MavX" className="h-12 w-auto" />
                    <p className="mt-4 text-sm text-white/50">Welcome back, please sign in to continue</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 px-8 py-10 backdrop-blur-md">
                    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                        {error && (
                            <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 px-4 py-3 text-sm text-rose-400">
                                {error}
                            </div>
                        )}
                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-medium text-white/80">Username</label>
                            <input
                                ref={usernameRef}
                                type="text"
                                autoComplete="username"
                                placeholder="Your username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                className={inputClass}
                                required
                            />
                        </div>
                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-medium text-white/80">Password</label>
                            <input
                                type="password"
                                autoComplete="current-password"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className={inputClass}
                                required
                            />
                        </div>
                        <Turnstile siteKey={SITE_KEY} onVerify={handleVerify} onExpire={handleExpire} resetKey={captchaReset} />
                        <button
                            type="submit"
                            disabled={loading || !captchaToken}
                            className="mt-2 w-full rounded-xl bg-rose-600 px-8 py-3 text-sm font-semibold text-white transition hover:bg-rose-700 active:bg-rose-800 disabled:opacity-50 disabled:cursor-not-allowed">
                            {loading ? "Signing in…" : "Sign In"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
